import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { 
  Package, Users, Building, ShoppingCart, FileText, Mail, 
  TrendingUp, AlertCircle, Clock 
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { adminApi, AdminStats } from '../api/admin';
import AdminLayout from '../components/AdminLayout';

export default function AdminDashboard() {
  usePageTitle("Administration — Tableau de bord — Lodiss");

  const { lang, setPage } = useApp();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-700';
      case 'confirmed': return 'bg-emerald-50 text-emerald-700';
      case 'shipped': return 'bg-blue-50 text-blue-700';
      case 'delivered': return 'bg-gray-50 text-gray-700';
      case 'cancelled': return 'bg-red-50 text-red-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Produits</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_products || 0}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {stats?.active_products || 0} actifs • {stats?.inactive_products || 0} inactifs
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-lodiss-red/10 flex items-center justify-center">
                <Package size={24} className="text-lodiss-red" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Utilisateurs</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_users || 0}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {stats?.buyer_users || 0} acheteurs • {stats?.reseller_users || 0} revendeurs
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div 
            className={`bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow ${
              (stats?.pending_reseller_applications || 0) > 0 
                ? 'border-amber-300 bg-amber-50/30' 
                : 'border-gray-200'
            }`}
            onClick={() => setPage('admin-reseller-applications')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Demandes revendeurs</p>
                <p className={`text-2xl font-bold ${(stats?.pending_reseller_applications || 0) > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                  {stats?.pending_reseller_applications || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">en attente</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${(stats?.pending_reseller_applications || 0) > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <Building size={24} className={(stats?.pending_reseller_applications || 0) > 0 ? 'text-amber-600' : 'text-gray-400'} />
              </div>
            </div>
          </div>

          <div 
            className={`bg-white rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow ${
              (stats?.unread_contact_messages || 0) > 0 
                ? 'border-red-300 bg-red-50/30' 
                : 'border-gray-200'
            }`}
            onClick={() => setPage('admin-contact')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Messages non lus</p>
                <p className={`text-2xl font-bold ${(stats?.unread_contact_messages || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {stats?.unread_contact_messages || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">messages</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${(stats?.unread_contact_messages || 0) > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                <Mail size={24} className={(stats?.unread_contact_messages || 0) > 0 ? 'text-red-600' : 'text-gray-400'} />
              </div>
            </div>
          </div>
        </div>

        {/* Second Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Devis en attente</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.pending_quote_requests || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                <FileText size={24} className="text-amber-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Commandes aujourd'hui</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.orders_today || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <ShoppingCart size={24} className="text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Revenus ce mois</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatPrice(stats?.revenue_this_month || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <TrendingUp size={24} className="text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Orders */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Commandes récentes</h2>
              <button 
                onClick={() => setPage('admin-orders')}
                className="text-sm text-lodiss-red hover:underline"
              >
                Voir tout
              </button>
            </div>
            <div className="space-y-3">
              {stats?.recent_orders?.slice(0, 5).map((order: any) => (
                <div key={order.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-mono text-sm font-medium">{order.order_number}</p>
                    <p className="text-xs text-gray-500">{order.user?.full_name || 'Client'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatPrice(order.total_amount || 0)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">Aucune commande</p>
              )}
            </div>
          </div>

          {/* Recent Quotes */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Devis récents</h2>
              <button 
                onClick={() => setPage('admin-quotes')}
                className="text-sm text-lodiss-red hover:underline"
              >
                Voir tout
              </button>
            </div>
            <div className="space-y-3">
              {stats?.recent_quotes?.slice(0, 5).map((quote: any) => (
                <div key={quote.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-mono text-sm font-medium">#{quote.id}</p>
                    <p className="text-xs text-gray-500">{quote.user?.full_name || 'Client'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{formatDate(quote.created_at)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(quote.status)}`}>
                      {quote.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!stats?.recent_quotes || stats.recent_quotes.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">Aucun devis</p>
              )}
            </div>
          </div>

          {/* Recent Applications */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Candidatures</h2>
              <button 
                onClick={() => setPage('admin-reseller-applications')}
                className="text-sm text-lodiss-red hover:underline"
              >
                Voir tout
              </button>
            </div>
            <div className="space-y-3">
              {stats?.recent_applications?.slice(0, 5).map((app: any) => (
                <div key={app.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{app.company}</p>
                    <p className="text-xs text-gray-500">{app.email}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      app.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                      app.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      {app.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!stats?.recent_applications || stats.recent_applications.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">Aucune candidature</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
